import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Clock, GraduationCap, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EnquireButton } from "@/components/EnquireButton";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="mx-auto max-w-5xl p-10"><Skeleton className="h-64" /></div>;
  if (!course) return <div className="p-10 text-center">Course not found. <Link to="/courses" className="underline">Browse all</Link></div>;

  const hasDiscount = course.discount_price != null && course.discount_price < course.price;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <Link to="/courses" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to courses
      </Link>
      <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
        <div>
          <div className="brand-gradient flex h-48 items-center justify-center overflow-hidden rounded-2xl">
            {course.image_url ? (
              <img src={course.image_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <GraduationCap className="h-20 w-20 text-white/70" />
            )}
          </div>
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">{course.category}</div>
            <h1 className="mt-2 font-display text-3xl font-bold">{course.name}</h1>
            <p className="mt-3 text-base text-muted-foreground">{course.short_description}</p>
          </div>
          {course.full_description && (
            <div className="mt-6 whitespace-pre-wrap text-sm leading-relaxed">{course.full_description}</div>
          )}
          {course.features?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-lg font-bold">What's included</h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {course.features.map((f, i) => (
                  <li key={`${i}-${f}`} className="flex items-start gap-2 rounded-lg border border-border/70 p-3 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[hsl(var(--brand-orange))]" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {course.subjects?.length > 0 && (
            <div className="mt-8" data-testid="course-subjects">
              <h2 className="font-display text-lg font-bold">Subjects & chapters included</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {course.subjects.map((s) => (
                  <div key={s.id} className="rounded-lg border border-border/70 p-3 text-sm">
                    <div className="flex justify-between font-semibold"><span>{s.name}</span><span className="text-xs font-normal text-muted-foreground">{s.question_count} questions</span></div>
                    {s.chapters?.length > 0 && <div className="mt-1 text-xs text-muted-foreground">{s.chapters.join(" · ")}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {course.prerequisites && (
            <div className="mt-6 rounded-lg border border-border bg-secondary/50 p-4 text-sm">
              <div className="font-semibold">Prerequisites</div>
              <div className="mt-1 text-muted-foreground">{course.prerequisites}</div>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-20 lg:self-start">
          <Card className="border-border/70">
            <CardContent className="p-6">
              <div>
                {course.plan_type === "free" ? (
                  <div className="font-display text-3xl font-black text-emerald-600">Free</div>
                ) : hasDiscount ? (
                  <>
                    <div className="flex items-end gap-2">
                      <div className="font-display text-3xl font-black">₹{course.discount_price.toLocaleString("en-IN")}</div>
                      <div className="pb-0.5 text-sm text-muted-foreground line-through">₹{course.price.toLocaleString("en-IN")}</div>
                    </div>
                    <div className="text-xs font-semibold text-emerald-600">
                      You save ₹{(course.price - course.discount_price).toLocaleString("en-IN")}
                    </div>
                  </>
                ) : (
                  <div className="font-display text-3xl font-black">₹{(course.price || 0).toLocaleString("en-IN")}</div>
                )}
              </div>
              <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
                {course.duration && <li className="flex items-center gap-2"><Clock className="h-4 w-4" /> {course.duration}</li>}
                {course.course_type && <li className="flex items-center gap-2 capitalize"><GraduationCap className="h-4 w-4" /> {course.course_type}</li>}
                {course.instructor_name && <li className="flex items-center gap-2">👤 {course.instructor_name}</li>}
              </ul>
              {course.buy_enabled !== false ? (
                <Button asChild className="mt-6 h-11 w-full bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="enroll-cta">
                  <Link to={`/checkout/${course.slug}`}>Buy Now</Link>
                </Button>
              ) : (
                <Button disabled className="mt-6 w-full" data-testid="enroll-closed">Enrollment closed</Button>
              )}
              <EnquireButton course={course.name} className="mt-2 w-full" testId="course-detail-enquire" />
              <div className="mt-3 text-center text-xs text-muted-foreground">{course.access_days ? `${course.access_days} days access` : "Lifetime access"} · Secure Razorpay checkout</div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}
